<!-- eslint-disable max-len -->
<template>
  <aha-modal @hide="hideModal()" :title="$i18nPresenter.t('Invite members')">
    <form @submit.prevent="onSubmitInvite()">
      <p v-if="false" class="text-base-80">
        {{ $i18nPresenter.t('Get your teammates on board to enhance collaboration.') }}
      </p>
      <div class="flex">
        <AhaInputTag
          class="mr-2"
          id="input-tag__invite-new-member"
          v-model="emails"
          :placeholder="emails.length > 0 ? '' : inputTagPlaceHolder"
          :validator="emailRegex"
          :disabled="isDisabledInviteeInput"
          :allow-duplicate="false"
        />
        <RoleSelection
          :style="{width: '32.5%'}"
          :invitedRole="invitedRole"
          @changeInvitedRole="changeInvitedRole"
        />
      </div>
      <div v-if="currentAccount.createdFrom !== 'presenter'">
        <div
          v-if="!isExpiredPlan"
          class="mb-2"
          :class="{
            'text-green-100': modalData.numberOfUsedLicenses < modalData.licenseQuantity,
          }"
        >
          <span class="font-bold">{{ $i18nPresenter.t('Licenses used:') }}</span>
          <span>
            {{ modalData.numberOfUsedLicenses | formatNumber }}/{{ modalData.licenseQuantity | formatNumber }}
          </span>
        </div>
        <div
          v-if="isExpiredPlan"
          class="mb-2 font-bold text-red-50"
        >
          {{ $i18nPresenter.t('Your licences have expired') }}
        </div>
        <p class="text-base-90" v-if="modalData.licenseQuantity > 1">
          {{ $i18nPresenter.t('For more licenses, please') }}
          <a
            class="font-bold text-primary-90"
            target="_blank"
            :href="modalData.myTeamFooterURL"
            @click="handleClickOnFooterTypeForm()"
          >{{ $i18nPresenter.t('contact our Sales team') }}</a>
          {{ $i18nPresenter.t('for the best deal.') }}
        </p>
        <p class="text-base-90" v-if="modalData.licenseQuantity === 1">
          {{ $i18nPresenter.t('You need additional licenses to proceed. Please') }}
          <a
            class="font-bold text-primary-90"
            target="_blank"
            :href="modalData.myTeamFooterURL"
            @click="handleClickOnFooterTypeForm()"
          >{{ $i18nPresenter.t('contact our Sales team') }}</a>
          {{ $i18nPresenter.t('to get the best deal for your team today.') }}
        </p>
      </div>
      <div v-if="showInviteError">
        <span class="block text-red-100 text-xs">{{ inviteErrorListEmail }}</span>
      </div>
      <div v-if="isShowMaxLimitSelfCreatTeamErr && currentAccount.createdFrom === 'presenter'">
        <span class="block text-red-100 text-xs">
          <span
            v-html="$i18nPresenter.t('yourTeamHasReachedXMember', {
              x: getTotalMemberText
            })"
          ></span>
          {{ $i18nPresenter.t('To add more, review your existing member list or') }}
          <span class="underline cursor-pointer" @click="contactSupport">{{ $i18nPresenter.t('contact our support team.') }}</span>
        </span>
      </div>
    </form>
    <template #action>
      <aha-link @click="hideModal()">
        <span class="font-bold">{{ $i18nPresenter.t('Cancel') }}</span>
      </aha-link>

      <vue-recaptcha
        :sitekey="keyRecaptcha"
        @verify="onVerify"
        @error="onRecaptchaError"
        size="invisible"
        @expired="onExpired"
        :load-recaptcha-script="true"
        recaptcha-host="www.recaptcha.net"
        ref="recaptcha"
        class="auth-captcha"
      />
      <aha-button
        ref="sendInvitationButton"
        has-margin-left
        type="submit"
        :disabled="isDisabledSendButton"
        :submitting="isSendingInvitations"
        @click="onSubmitInvite()"
      >
        {{ $i18nPresenter.t('Send invitation') }}
      </aha-button>
    </template>
  </aha-modal>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import moment from 'moment'
// inspiration: https://learnvue.co/2020/01/build-a-custom-vuejs-tag-input-in-under-10-minutes/

import { mapState } from 'vuex'
import InvitationApi from '@/api/invitation'
import utils from '@/util'
import Config from '@/config'
import eventCategories from '@/constant/eventCategories'
import RoleSelection from '@/components-v2/my-team/RoleSelection.vue'

export default {
  components: {
    VueRecaptcha,
    RoleSelection,
  },
  props: {
    modalData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      emails: [],
      isSendingInvitations: false,
      showInviteError: false,
      inviteErrorListEmail: '',
      emailRegex: "^(([^<>()[\\]\\.,;:\\s@']+(\\.[^<>()[\\]\\.,;:\\s@']+)*)|('.+'))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",
      keyRecaptcha: Config.keyRecapcha,
      tokenRecaptcha: '',
      invitedRole: 'editor',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
    }),
    getTotalMemberText() {
      if (this.currentAccount && this.currentAccount.licenseQuantity) return this.currentAccount.licenseQuantity
      return 200
    },
    isDisabledInviteeInput() {
      if (this.modalData.numberOfUsedLicenses + this.emails.length
        >= this.modalData.licenseQuantity) return true
      return false
    },
    isDisabledSendButton() {
      const hasInput = this.isInputTagHasValue || this.emails.length > 0
      return !hasInput || (this.modalData.numberOfUsedLicenses >= this.modalData.licenseQuantity)
    },
    isShowMaxLimitSelfCreatTeamErr() {
      return (this.modalData.numberOfUsedLicenses >= this.modalData.licenseQuantity)
    },
    currentUserIsTeamOwner() {
      return this.currentUser.accountId && this.currentUser.accountRole === 'owner'
    },
    isExpiredPlan() {
      return moment().isAfter(moment(this.modalData.planEndAt), 'minute')
    },
    inputTagPlaceHolder() {
      return this.$i18nPresenter.t('Emails, comma separated')
    },
  },
  mounted() {
    setTimeout(() => {
      this.focusInviteeInput()
    }, 150)
  },
  methods: {
    onVerify(response) {
      this.tokenRecaptcha = response
      this.handleSendInvitation()
    },
    onSubmitInvite() {
      if (this.isSendingInvitations) return
      this.isSendingInvitations = true
      this.$refs.recaptcha.execute()
      utils.removeRecaptchaCover()
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },

    onRecaptchaError() {
      this.isSendingInvitations = false
    },
    focusInviteeInput() {
      const input = document.getElementById('input-tag__invite-new-member').children[0]
      if (!input) return
      input.focus()
    },

    handleClickOnFooterTypeForm() {
      if (this.currentUserIsTeamOwner) {
        utils.mixpanelTracking(
          eventCategories.MY_TEAM,
          'Team management > Invite modal > Owner clicks on typeform link',
        )
      } else {
        utils.mixpanelTracking(
          eventCategories.MY_TEAM,
          'Team management > Invite modal > Casual user clicks on typeform link',
        )
      }
    },

    async handleSendInvitation() {
      if (!this.isInputTagHasValue && this.emails.length === 0) {
        utils.showToastError(this.$i18nPresenter.t('Please enter member\'s email'))
        this.focusInviteeInput()
        this.tokenRecaptcha = ''
        this.$refs.recaptcha.reset()
        return
      }
      this.isSendingInvitations = true
      // Because we have 2 elements listen to the Enter key at the same
      // time (the form & the input tag),
      // setTimeout a bit for waiting the input tag (emails) to be added after Enter pressed
      setTimeout(async () => {
        try {
          utils.mixpanelTracking(
            eventCategories.MY_TEAM,
            'Team management > Invite member > Send invitation',
            this.emails.toString(),
            '',
            {
              plan: this.$store.state.currentUser.plan,
              isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
            },
          )
          await InvitationApi.createInvitations({
            emails: this.emails.map((email) => email.toLowerCase()),
            tokenRecaptcha: this.tokenRecaptcha,
            accountRole: this.invitedRole,
          })
          this.$emit('hide')
          this.$emit('updateInvitations', this.emails.length)
          utils.mixpanelTracking(
            eventCategories.MY_TEAM,
            'Team management > Invite member > Invited successfully',
            this.emails.toString(),
            '',
            {
              plan: this.$store.state.currentUser.plan,
              role: this.invitedRole,
              isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
            },
          )
          this.tokenRecaptcha = ''
          this.$refs.recaptcha.reset()
          utils.showToastSuccess(this.$i18nPresenter.t('Invited successfully'))
        } catch (error) {
          utils.mixpanelTracking(
            eventCategories.MY_TEAM,
            'Team management > Invite member > Invite failed',
            error.body.message,
            '', {
              isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
            },
          )
          this.showInviteError = true
          if (error.body.emails) {
            if (this.$store.state.currentAccount.createdFrom === 'presenter') {
              this.inviteErrorListEmail = `${this.$i18nPresenter.t('This emails is already has team:')} ${error.body.emails.join(', ')}`
            } else this.inviteErrorListEmail = `${this.$i18nPresenter.t('This emails is already has team or plan:')} ${error.body.emails.join(', ')}`
          } else if (error.body.message) this.inviteErrorListEmail = error.body.message
          else utils.showToastError(this.$i18nPresenter.t('Something went wrong, please check your connection or contact us for support'))
          this.tokenRecaptcha = ''
          this.$refs.recaptcha.reset()
        } finally {
          this.isSendingInvitations = false
        }
      }, 300)
    },
    hideModal() {
      utils.mixpanelTracking(
        eventCategories.MY_TEAM,
        'Team management > Invite member > Cancel invitation',
        this.emails.toString(),
        '',
        {
          plan: this.$store.state.currentUser.plan,
          isSelfCreatedTeam: this.$store.state.currentAccount.createdFrom === 'presenter',
        },
      )
      this.$emit('hide')
    },
    contactSupport() {
      window.open('https://form.typeform.com/to/sC0I4AXE', '_blank')
    },
    changeInvitedRole(value) {
      this.invitedRole = value
    },
  },
}
</script>
<style scoped lang='scss'>
</style>
